import { useIntl } from 'react-intl';
import useStore from 'Hooks/useStore';
import {
	isTripClosedByBookingType,
	TicketsTabTrip,
} from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { useAvailability } from './useAvailability';
import { legClash } from './legClash';
import { useIsPaxSpaceExceeded } from './useIsPaxSpaceExceeded';
import { useIsSoldOut } from './useIsSoldOut';
import { useIsTrailerSpaceExceeded } from './useIsTrailerSpaceExceeded';
import { useIsVehicleSpaceExceeded } from './useIsVehicleSpaceExceeded';
import { isWalkOnNotOffered } from './walkOnNotOffered';

export const useLegErrorMessage = (
	wizardData: BookingWizardData,
	trip: TicketsTabTrip,
	legType: 'departure' | 'return',
) => {
	const store = useStore();
	const { formatMessage } = useIntl();
	const availability = useAvailability(trip);
	const isSoldOut = useIsSoldOut(wizardData.tabSelected, availability.passenger, availability.vehicle);
	const isPaxSpaceExceeded = useIsPaxSpaceExceeded(trip.passengerSpacesAvailable, wizardData);
	const isVehicleSpaceExceeded = useIsVehicleSpaceExceeded(trip.vehicleSpacesAvailable, wizardData);
	const isTrailerSpaceExceeded = useIsTrailerSpaceExceeded(trip.trailerSpotsAvailable, wizardData);

	if (!store.isStaff) {
		// Only visitor/customer users can see the following messages
		if (trip.departed) {
			return formatMessage({ id: 'general__departed__label', defaultMessage: 'Departed' })?.toUpperCase();
		}
		/*
			Closed for the booking being made, not closed to everybody.

			`isTripCompletelyClosedFromBookings` asks whether the sailing is shut to every kind of
			booking, which on a trip with vehicles closed and passengers open is false — so a
			customer on the vehicle tab got no message at all, while `useIsLegVisuallyDisabled`
			refused the click using this predicate. The row read as an ordinary priced sailing with
			space on it that simply did nothing. Both now ask the same question.
		*/
		/*
			A vessel that takes no walk-ons says so on its passenger capacity, not across the row.

			`CLOSED` here replaced the capacities — the red passenger chip carrying the reason among
			them — and its explanation told the customer to contact staff to book, which for a vessel
			that never carries walk-ons is a phone call that ends in no. Nothing is returned, so the
			row keeps its chips; it is still refused, by `useIsLegSelectable`, which asks
			`isTripClosedByBookingType` itself.
		*/
		if (isWalkOnNotOffered(trip, wizardData.tabSelected)) {
			return undefined;
		}
		if (isTripClosedByBookingType(trip, wizardData.tabSelected)) {
			return 'CLOSED';
		}
	}

	if (!store.can('OverbookInBookingWizard')) {
		if (isSoldOut) {
			return 'SOLD OUT';
		}
		if (wizardData.tabSelected === 'vehicle' && wizardData.innerWizardData.type === 'vehicle') {
			const { innerWizardData } = wizardData;
			const { trailerCheckboxSelected, trailerLengthId } = innerWizardData;

			// Only show when they have selected their trailer length
			if (trailerCheckboxSelected && trailerLengthId && isTrailerSpaceExceeded) {
				if (availability.measurementType === 'LENGTH') {
					return 'TRAILER LENGTH EXCEEDS SPACES AVAILABLE';
				}
				if (availability.measurementType === 'WEIGHT') {
					return 'TRAILER WEIGHT EXCEEDS SPACES AVAILABLE';
				}
				// #trailer-checkbox-deselect-on-no-trailer
				// Adding this tag as it could affect trailer overbooking error message
			}
			if (isVehicleSpaceExceeded) {
				if (availability.measurementType === 'LENGTH') {
					return 'VEHICLE LENGTH EXCEEDS SPACES AVAILABLE';
				}
				if (availability.measurementType === 'WEIGHT') {
					return 'VEHICLE WEIGHT EXCEEDS SPACES AVAILABLE';
				}
			}
		}

		if (isPaxSpaceExceeded) {
			return 'NO SPACES AVAILABLE';
		}
	}

	switch (legClash(trip, wizardData, legType)) {
		case 'SAME_TRIP':
			return legType === 'departure'
				? 'Cannot book on same returning trip'
				: 'Cannot book on same departing trip';
		case 'AFTER_RETURN':
			return 'This time is after your return date';
		case 'BEFORE_DEPARTURE':
			return 'This time is before your departure date or time';
		default:
			break;
	}

	return undefined;
};
